import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
import {
  BucketUploadResponse,
  dataURLtoFile,
  getExtension,
  nameVesselImageFile,
  getFileNameWithExtension,
  getImgSize,
  getNameWithoutExtension,
  getUniqueFileName,
  ImageMeta,
  resizeImage,
} from '../../../core/pure-functions/upload/image-upload-helper';
import { mergeMap } from 'rxjs/operators';
import { combineLatest, Subscription } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { UploadDAO } from '../../../core/services/dao/uploadDAO';
import { VesselImageDAO } from '../../../core/services/dao/vesselImageDAO';
import { ContainsSubscriptions } from '../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import * as uuid from 'uuid';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { VesselDAO } from '../../../core/services/dao/vesselDAO';

@Component({
  selector: 'app-delete-dialog',
  templateUrl: './image-cropper-dialog.component.html',
  styleUrls: ['./image-cropper-dialog.component.scss'],
})
export class ImageCropperDialogComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  croppedImage: string;
  showCropper = false;
  subs: Subscription[] = [];
  cropClicked = false;
  transform: ImageTransform = {};
  scale = 1;
  canvasRotation = 0;
  uploadResponses: BucketUploadResponse[];
  uniqImageUrl: string;
  vessel: Partial<Vessel> = {
    lengthFt: 0,
    make: '',
    model: '',
    mooringLocation: {
      id: null,
      vesselId: null,
      address: null,
      lat: null,
      long: null,
      parkingAddress: null,
      parkingLat: null,
      parkingLong: null,
      mooringAddressComponents: null,
      parkingAddressComponents: null,
    },
  };
  aspectRatioLeft: number = 16;
  aspectRatioRight: number = 9;
  landscape: boolean = true;
  portrait: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ImageCropperDialogComponent>,
    public uploadDAO: UploadDAO,
    public vesselImageDao: VesselImageDAO,
    public vesselDao: VesselDAO,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit() {
    const vesselSub = this.vesselDao
      .loadByIdForBuyflow(this.data.vesselId)
      .subscribe((vessel) => {
        this.vessel = vessel;
        if (
          !vessel.lengthFt ||
          !vessel.make ||
          !vessel.model ||
          !vessel.mooringLocation
        ) {
          this.cropClicked = true;
        }
      });
    this.subs.push(vesselSub);
    console.log(this.data.image);
    this.uniqImageUrl = this.data.image.url + '?noCache=' + uuid.v4();
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  setPortraitOrLandscape() {
    if (this.portrait) {
      this.portrait = false;
      this.landscape = true;
      this.aspectRatioLeft = 16;
      this.aspectRatioRight = 9;
    } else if (this.landscape) {
      this.portrait = true;
      this.landscape = false;
      this.aspectRatioLeft = 9;
      this.aspectRatioRight = 16;
    }
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }

  imageLoaded() {
    this.showCropper = true;
  }

  async saveCroppedImage() {
    const croppedAsFile = dataURLtoFile(
      this.croppedImage,
      getFileNameWithExtension(this.data.image.url),
    );
    let imageMeta = await combineLatest<ImageMeta>(
      getImgSize(croppedAsFile),
    ).toPromise();
    if (imageMeta[0]) {
      const ext = getExtension(imageMeta[0].originalFile.name);
      const fileName = `${nameVesselImageFile(this.vessel)}-${getUniqueFileName(
        ext,
      )}`;

      let imageUrl;
      if (this.landscape) {
        imageUrl = await resizeImage(
          URL.createObjectURL(imageMeta[0].originalFile),
          1280,
          720,
        );
      } else {
        imageUrl = await resizeImage(
          URL.createObjectURL(imageMeta[0].originalFile),
          720,
          1280,
        );
      }
      const image = await fetch(imageUrl)
        .then((r) => r.blob())
        .then(
          (blobFile) => new File([blobFile], fileName, { type: 'image/png' }),
        );

      this.uploadPhotosAndUpdateAssociation(image, fileName, this.data.oldId);

      // this.subs.push(combinedResize);
    }
  }

  private uploadPhotosAndUpdateAssociation(
    file: File,
    fileName: string,
    imageId: number,
  ): void {
    const uploadSub = combineLatest([
      this.uploadDAO.uploadVesselImage(file, fileName, this.data.vesselId),
    ])
      .pipe(
        mergeMap((responses) => {
          this.uploadResponses = responses;
          return this.vesselImageDao.updateFromPartial(imageId, {
            url: responses[0].url,
            resourceKey: responses[0].key,
            thumbUrl: responses[0].url,
            thumbKey: responses[0].key,
            vesselId: this.data.vesselId,
            needsCrop: false,
            sortOrder: this.data.image.sortOrder,
          });
        }),
      )
      .subscribe(
        (createdAssociation) => {
          if (createdAssociation) {
            this.dialogRef.close(this.uploadResponses);
          }
        },
        (error1) => {
          console.log(error1);
        },
      );
    this.subs.push(uploadSub);
  }
  flipHorizontal() {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH,
    };
  }

  flipVertical() {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV,
    };
  }

  zoomIn() {
    this.scale += 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  zoomOut() {
    if (this.scale !== 1) {
      this.scale -= 0.1;
      this.transform = {
        ...this.transform,
        scale: this.scale,
      };
    }
  }
}
